/**
 * <SignatureBow> — le nœud papillon de l'avatar signé (privé au dossier `Avatar` : la capsule du
 * tuteur et `<ChampionAvatar>` le partagent, c'est ce qui tient leur nœud à la même place).
 *
 * Brand core V13, chap. 2 et 11 : queue d'aronde, deux ailes échancrées et un nœud arrondi, la
 * moitié basse plus sombre pour que ça se lise comme du tissu même à 34 px. Un aplat SVG, jamais
 * un rendu 3D — le livre range explicitement l'objet 3D dans les « jamais ».
 *
 * Géométrie reprise telle quelle de la maquette cliquable (`SignedAvatar.tsx`), `viewBox`
 * 320 × 160 : largeur = 45 % de l'avatar, hauteur = moitié de la largeur, base à −7,5 % sous le
 * bord du cercle (kit V16, capsule du tuteur).
 *
 * Jaune signature pour un tuteur, toujours — la V13 a retiré la couleur du champion de la capsule.
 * L'appelant passe les deux jetons déjà résolus : ce fichier ne lit pas le thème, `<Avatar>` et
 * `<ChampionAvatar>` le font pour lui.
 */
import React from "react"
import Svg, { Path, Rect } from "react-native-svg"

/** Part de la taille de l'avatar occupée par le nœud. */
const WIDTH_RATIO = 0.45
/** Base du nœud sous le bord du cercle, en fraction de la taille (kit V16 : −7,5 %). */
const OVERHANG_RATIO = 0.075
/** Le pli des ailes : le ton foncé, à mi-force. */
const FOLD_OPACITY = 0.55

export type SignatureBowProps = {
    /** Diamètre de l'avatar, en px. */
    size: number
    /** Couleur pleine des ailes (valeur résolue, pas un jeton). */
    color: string
    /** Couleur du pli et du nœud central (valeur résolue, pas un jeton). */
    shade: string
    /**
     * Opacité de `shade` sur `color`. Défaut 1 : `shade` est un ton plein (le jaune a le sien).
     * En dessous, `shade` est un VOILE : une couleur qui n'a pas de ton foncé en jeton (le costume
     * d'un champion) garde sa teinte dans le pli et le nœud central, juste assombrie.
     */
    shadeOpacity?: number
}

export function SignatureBow(props: SignatureBowProps) {
    const { size, color, shade, shadeOpacity = 1 } = props

    const width = size * WIDTH_RATIO
    const height = width / 2

    return (
        <Svg
            viewBox="0 0 320 160"
            width={width}
            height={height}
            pointerEvents="none"
            style={{
                position: "absolute",
                bottom: -size * OVERHANG_RATIO,
                left: (size - width) / 2,
            }}
        >
            <Path d="M136 60 L0 22 L38 80 L0 138 L136 100 Z" fill={color} />
            <Path d="M184 60 L320 22 L282 80 L320 138 L184 100 Z" fill={color} />
            <Path d="M136 80 L38 80 L0 138 L136 100 Z" fill={shade} opacity={FOLD_OPACITY * shadeOpacity} />
            <Path d="M184 80 L282 80 L320 138 L184 100 Z" fill={shade} opacity={FOLD_OPACITY * shadeOpacity} />
            {/* Un voile se pose sur la couleur des ailes, pas sur ce qui passe derrière le nœud. */}
            {shadeOpacity < 1 && <Rect x={128} y={44} width={64} height={72} rx={18} fill={color} />}
            <Rect x={128} y={44} width={64} height={72} rx={18} fill={shade} opacity={shadeOpacity} />
        </Svg>
    )
}
